import { formatDate, formatPrice } from '../../../../shared/js/formatters'
import {
  computeOptionsCents,
  computeOptionsPreparationDays,
  computeOptionsToParams,
} from '../../../../shared/js/options-utils'
import jewelleryBagPicture from '../../../images/jewellery-bag.avif'
import { notifyCartBadge } from '../Cart'
import CartDialog from '../purchase-process/cart/CartDialog'
import Dialog from '../shared/Dialog'
import SaleCountdown from '../shared/SaleCountdown'
import AlmaWidget from './AlmaWidget'
import Option from './options/Option'
import Ratings from './Ratings'
import StockAlertButton from './StockAlertButton'
import ToggableDt from './ToggableDt'
import Variants from './Variants'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import type { Flash } from '../../../../back/v2/js/shared/Module'
import type {
  RatingsProps,
  SaleProps,
  VariantProps,
  VariantsProps,
} from '../../shared/Module'
import type { OptionComponentProps } from './options/Option'
import type { SyntheticEvent } from 'react'

// import OptionsSummaryLabel from '../../../../shared/js/components/OptionsSummaryLabel'
import { put } from '../../../../shared/js/json-fetch'
import signetRingBagPicture from '../../../images/signet-ring-bag.avif'

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void
  }
}

interface DescriptionProps {
  addToCartURL: string
  alma?: {
    merchantId: string
    minCents: number
    maxCents: number
  }
  authorized: boolean
  cart: {
    cents: number
    quantity: number
    url: string
  }
  customerEmail?: string
  family: {
    title: string
    products: Array<{
      active: boolean
      id: number
      name: string
      url: string
      kind: string
    }>
  }
  guide?: {
    // content: string
    title: string
    url: string
    // Custom page attached file
    pdf?: string
  }
  guideHTML: string
  // Product dedicated attached PDF
  guidePDF?: string
  // htmlPicture: string
  id: number
  initialOptions: OptionComponentProps[]
  initialVariants: VariantsProps
  jewelleryBag: boolean
  name: string
  ratings: RatingsProps
  relatedProductsHTML?: string
  sale?: SaleProps
  shippingOfferedThreshold: number
  shortDescription: string
  stocklessSellable: boolean
}

const CHAIN_REGEX = /cha(î|i)ne/i

// Display product description.
// Manage price update depending on selected options and quantity.
// Triggers add to cart: post data using fetch API.
const Description = ({
  addToCartURL,
  alma,
  authorized,
  cart,
  customerEmail,
  guide,
  guideHTML,
  guidePDF,
  // htmlPicture,
  family,
  id,
  jewelleryBag,
  name,
  initialOptions = [],
  initialVariants,
  relatedProductsHTML,
  ratings,
  sale,
  shippingOfferedThreshold,
  shortDescription,
  stocklessSellable,
}: DescriptionProps) => {
  const [cartQuantity, setCartQuantity] = useState(cart.quantity)
  const [dialog, setDialog] = useState('')
  const [pending, setPending] = useState(false)
  const [variants, setVariants] = useState(initialVariants?.variants ?? [])
  const [flash, setFlash] = useState<Flash | null>(null)
  const [options, setOptions] = useState(initialOptions)
  const [variantsAlerts, setVariantsAlerts] = useState<number[]>([])

  // Find the current selected variant.  Defaults to first entry
  const selectedVariant =
    variants.find(({ selected }) => selected) || variants[0]
  const {
    cents,
    id: selectedVariantId,
    purchasable,
    regularCents = 0,
    frontReference,
    reference,
    shipping: { deliveryDate },
  } = selectedVariant
  const { optionsCents, optionsTaxCents } = computeOptionsCents(options)
  const totalCents = cents + optionsCents + optionsTaxCents
  const totalRegularCents = regularCents + optionsCents + optionsTaxCents
  // Check cart actual cents, selected variant and options cents for free shipping eligibility
  // const freeShipping = cart.cents + totalCents >= shippingOfferedThreshold
  const sellable = stocklessSellable || selectedVariant.quantity > 0
  const addableToCart = sellable && !pending && purchasable && authorized

  // Is there any mandatory option that is not selected nor filled yet?
  const hasMissingRequiredOptions = hasMissingRequiredOptionValues(options)
  const disableAddToCart =
    variantsAlerts?.includes(selectedVariantId) || hasMissingRequiredOptions

  // Delivery label
  const optionsPreparationDays = computeOptionsPreparationDays(options)
  const finalDeliveryDate =
    optionsPreparationDays > 0
      ? dayjs(deliveryDate).add(optionsPreparationDays, 'days')
      : deliveryDate
  const formattedDate = finalDeliveryDate
    ? formatDate(finalDeliveryDate.toString())
    : null
  const deliveryLabel = formattedDate
    ? `Livraison à partir ${/\d/.test(formattedDate) ? 'du ' + formattedDate : 'de ' + formattedDate.toLocaleLowerCase()}`
    : null

  const guideLink = guide && (
    <a
      aria-haspopup='dialog'
      className='panel-btn'
      href={guide.url}
      onClick={openGuideDialog}
    >
      Guide
    </a>
  )
  // Options are disabled when product is not purchasable or when customer is
  // currently adding it to the cart.
  const optionsDisabled = !addableToCart || pending

  // Manage product thumb global link instead of "h2" link
  useEffect(() => {
    document.querySelectorAll('.card').forEach((card) => {
      card.addEventListener('click', (e) => {
        e.stopPropagation()
        e.preventDefault()

        const link = card.querySelector('a')
        if (!link) return

        const url = link.getAttribute('href')
        if (!url) return

        if (link.getAttribute('target') === '_blank') {
          window.open(url, '_blank')
          return
        }
        window.location.href = url
      })
    })
  }, [])

  const isChain = CHAIN_REGEX.test(name)
  const isSignetRing = name.match(/chevalière/i)

  return (
    <>
      <h1 className='h1'>{name}</h1>

      {sale && <SaleCountdown {...sale} />}
      <div className='product-meta'>
        <div className='price-wrapper'>
          <p className='price'>
            <ins>{formatPrice(totalCents)}</ins>
            {regularCents > 0 && <del>{formatPrice(totalRegularCents)}</del>}

            {/* <span>
            ou 3 x 116,33€
            <button
              className='tooltip-trigger'
              aria-describedby='tooltip-paymentplan-tooltip'
            >
              <span className='visually-hidden'>En savoir plus</span>
              <span
                className='tooltip'
                role='tooltip'
                id='tooltip-paymentplan-tooltip'
              >
                ● Paiement en 3 fois sans frais proposé à partir de 350€ d’achat
                <br />
                <br />● Vous étalez votre paiement sur 3 mois consécutifs
              </span>
            </button>
          </span> */}
          </p>
          {alma?.merchantId &&
            alma.minCents <= totalCents &&
            alma.maxCents > totalCents && (
              <AlmaWidget merchantId={alma.merchantId} cents={totalCents} />
            )}
        </div>
        {(frontReference || reference) && (
          <p className='ref'>
            <abbr title='Référence produit'>réf.</abbr>
            {` ${frontReference || reference}`}
          </p>
        )}

        {ratings && <Ratings {...ratings} />}
        {/* {freeShipping && <strong>livraison offerte</strong>} */}
      </div>

      <form onSubmit={addToCart}>
        <dl className='accordion'>
          {/* Family dropdown (if applicable); clicks redirect to related product pages */}
          {family?.products?.length > 1 && (
            <>
              <ToggableDt expanded>{family.title}</ToggableDt>
              <dd>
                <ul>
                  {family.products.map(
                    ({ active, id: prodId, name: prodName, kind, url }) => (
                      <li key={`family-${prodId}`}>
                        {active ? (
                          <span
                            className={`color-filter ${kind ? kind : ''}${active ? ' current' : ''}`}
                          >
                            {prodName}
                          </span>
                        ) : (
                          <a
                            href={url}
                            key={prodId}
                            className={`color-filter ${kind ? kind : ''}`}
                          >
                            {prodName}
                          </a>
                        )}
                      </li>
                    )
                  )}
                </ul>
              </dd>
            </>
          )}

          {/* Variant selection, if applicable */}
          {variants && (
            <Variants
              {...initialVariants}
              {...variants}
              displayWeight={false}
              selectedId={selectedVariantId}
              onChange={setVariants}
              guideLink={guideLink}
              isChain={isChain}
            />
          )}
        </dl>

        <div id='options-summary-portal' />
        {deliveryLabel && (
          <strong className='delivery-label'>{deliveryLabel}</strong>
        )}
        {/* "buttons" is used for fixed position on mobile */}
        <div className='buttons options'>
          {options.map((option) => (
            <Option
              {...option}
              key={option.id}
              onCancel={handleOptionCancel}
              onChange={handleOptionChange}
              disabled={optionsDisabled}
              portalId='options-summary-portal'
              hidePrice
            />
          ))}
          <AddToCartButton
            addableToCart={addableToCart}
            customerEmail={customerEmail}
            pending={pending}
            onCreatedAlert={onCreatedAlert}
            variantId={selectedVariantId}
            disabled={disableAddToCart}
            cents={totalCents}
            title={
              hasMissingRequiredOptions
                ? 'Vous devez choisir une option'
                : undefined
            }
          />
          {/* Cart existing items for this product, and action buttons: option selection, add-to-cart proper */}
          {cartQuantity > 0 && (
            <div className='already-added'>
              <small>
                Déjà ajouté {cartQuantity > 1 ? `${cartQuantity} fois` : ''} à
                votre panier
              </small>
            </div>
          )}
        </div>
        {flash && (
          <div
            className={`c-message c-message--${flash.kind}`}
            dangerouslySetInnerHTML={{ __html: flash.message }}
          ></div>
        )}
      </form>

      {/* Description and delivery info */}
      <dl className='accordion product-info'>
        <ToggableDt>Description</ToggableDt>
        <dd>
          <div dangerouslySetInnerHTML={{ __html: shortDescription }} />
          {/* {isChain && <DataTables {...initialVariants} />} */}
          <DataTables {...initialVariants} />
        </dd>
      </dl>

      <dl className='accordion product-info'>
        <ToggableDt>Garantie</ToggableDt>
        <dd>
          <p>
            Votre satisfaction est notre priorité. Chaque création est couverte
            contre tout vice ou défaut caché pendant deux ans à compter de la
            date d'achat. Si votre bijou ne vous convient pas, vous disposez
            d’un délai de 14 jours pour le retourner (à l’exception des bijoux
            avec gravure ou créations sur-mesure). Une question ? Notre équipe
            vous répond avec joie.
          </p>
        </dd>
      </dl>

      {jewelleryBag && (
        <dl className='accordion product-info'>
          <ToggableDt>Écrin & certificat</ToggableDt>
          <dd>
            <p>
              Chaque création Maison la Couronne est livrée dans un écrin
              soigné, accompagnée d'un certificat d'authenticité. Sur demande,
              un coffret est disponible pour en faire un cadeau d'exception.
            </p>
          </dd>
        </dl>
      )}

      {/* Join tag */}
      <div id={`widget-lc-${id}`} style={{ marginTop: '1.5rem' }} />

      {guidePDF && (
        <p className='feature-block guide-block'>
          <span className='feature-title'>
            Vous ne connaissez pas votre taille ?{' '}
          </span>
          <a
            href={guidePDF}
            target='_blank'
            className='guide-link mlc-btn mlc-btn-type-2'
          >
            Imprimez notre guide des tailles
          </a>
        </p>
      )}

      <div className='feature-block'>
        <h2 className='feature-title'>Les avantages de Maison La Couronne</h2>
        <ul>
          <li className='gift-box'>
            <span>Coffret Cadeau</span>
          </li>
          <li className='hand-etching'>
            <span>Atelier de gravure français</span>
          </li>
          <li className='secure-payment'>
            <span>Paiement sécurisé</span>
          </li>
          {shippingOfferedThreshold > 0 && (
            <li className='free-shipping'>
              <span>
                Livraison offerte à partir de {shippingOfferedThreshold} €
                d'achat
              </span>
            </li>
          )}
          {/* <li className='loyalty-card'>
            <span>Carte de fidélité</span>
          </li> */}
        </ul>
      </div>

      {jewelleryBag && (
        <div className='jewellery-picture'>
          <img
            alt='Photo d’un écrin'
            src={isSignetRing ? signetRingBagPicture : jewelleryBagPicture}
            aria-describedby='jewellery-bag-description'
          />
          <span id='jewellery-bag-description'>
            {isSignetRing
              ? 'Votre chevalière armoriée est livrée dans un coffret accompagné d’un écrin, d’un certificat d’authenticité et d’un sceau de cire personnalisé.'
              : 'Votre bijou est présenté dans un coffret comprenant un écrin, un certificat d’authenticité et une carte dédiée'}
          </span>
        </div>
      )}

      {/* Actual size guide contents (triggered by earlier CTA) */}
      {guide && guideHTML && (
        <Dialog
          className='right-modal--large right-action-modal'
          onCancel={closeDialog}
          opened={dialog === 'guide'}
          title={guide.title}
          id='guide-dialog'
          actions={
            <div className='right-action-modal__btn'>
              {guide.pdf && (
                <a
                  className='mlc-btn mlc-btn-type-3'
                  href={guide.pdf}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  Imprimer le guide des tailles
                </a>
              )}
              <a
                className='mlc-btn mlc-btn-type-1'
                href='/page/nos-guides'
                rel='noopener noreferrer'
              >
                Découvrez nos autres guides
              </a>
            </div>
          }
        >
          <div dangerouslySetInnerHTML={{ __html: guideHTML }} />
        </Dialog>
      )}

      {/* Add-to-cart confirmation dialog, with related products and all */}
      <CartDialog
        opened={dialog === 'add-to-cart'}
        onClose={closeAddToCartDialog}
        title='Cet article a été ajouté à votre panier'
        cartViewURL={cart.url}
        relatedProductsHTML={relatedProductsHTML}
      />
    </>
  )

  async function addToCart(event: SyntheticEvent) {
    event?.preventDefault()
    setPending(true)

    // Post update to the server. Expected params must look like this:
    // line_item[product_options][selection][*optionId*]: *optionValue (id or text)*
    const { data, error, flash } = await put(addToCartURL, {
      property_set_id: selectedVariantId,
      line_item: {
        product_options: { selection: computeOptionsToParams(options) },
      },
    })
    if (error) {
      setFlash(flash)
      return
    }

    const size = cartQuantity + 1
    setCartQuantity(size)
    if (data?.size) {
      notifyCartBadge(data.size)
    }
    setPending(false)
    setDialog('add-to-cart')

    // Trigger meta pixel tag event
    if (data?.lineItemId) {
      ;(window as Window & { fbq?: (...args: unknown[]) => void }).fbq?.(
        'track',
        'AddToCart',
        {},
        { eventID: data.lineItemId }
      )
    }
  }

  // Massage selected options to get a user readable summary
  // function computeSummary() {
  //   const labels = formatOptionsSummary(options)
  //   if (labels.length > 0) {
  //     return labels.map(([title, label], index) => (
  //       <div key={`${title}-${index}`}>
  // <OptionsSummaryLabel title={title} label={label} />
  //         &nbsp;-&nbsp;
  //         {formatPrice(optionsCents + optionsTaxCents)}
  //       </div>
  //     ))
  //   }
  // }

  function closeAddToCartDialog(event: SyntheticEvent) {
    closeDialog(event)
    setOptions(initialOptions)
  }

  function closeDialog(event: SyntheticEvent) {
    event?.preventDefault()
    setDialog('')
  }

  // Reset a given option to its initial value
  function handleOptionCancel(optId: number) {
    const values = initialOptions?.find((option) => option.id === optId)?.values
    if (!values) return

    const updatedOptions = options.map((option) =>
      option.id === optId ? { ...option, values } : option
    )
    setOptions(updatedOptions)
  }

  // Dynamically store filled values for expected option
  function handleOptionChange(optId: number, values: any) {
    const updatedOptions = options.map((option) =>
      option.id === optId ? { ...option, values } : option
    )
    setOptions(updatedOptions)
  }

  function openGuideDialog(event: SyntheticEvent) {
    event?.preventDefault()
    setDialog('guide')
  }

  function onCreatedAlert(variantId: number) {
    setVariantsAlerts([...variantsAlerts, variantId])
  }

  function hasMissingRequiredOptionValues(options: OptionComponentProps[]) {
    return !options.every((option) => {
      if (!option.required) return true

      if (option.kind === 'literal') {
        const value =
          option.values.find(({ selected }) => selected) || option.values[0]
        return Boolean(value?.text)
      }

      if (option.kind === 'selection') {
        return option.values.some(({ selected }) => selected)
      }

      return true
    })
  }
}

export default Description

// --------------------------------------------------------------------

interface AddToCartButtonProps {
  addableToCart: boolean
  customerEmail?: string
  disabled: boolean
  pending: boolean
  onCreatedAlert: Function
  variantId: number
  cents: number
  title?: string
}

function AddToCartButton({
  addableToCart,
  customerEmail,
  disabled,
  pending,
  onCreatedAlert,
  variantId,
  cents,
  title,
}: AddToCartButtonProps) {
  if (addableToCart) {
    return (
      <button
        className='mlc-btn mlc-btn-type-3 panel-btn'
        type='submit'
        disabled={disabled}
      >
        {title ? (
          title
        ) : (
          <>
            {/* <span>{formatPrice(cents)}</span> */}
            Ajouter au panier
          </>
        )}
      </button>
    )
  }

  if (pending) {
    return (
      <button className='mlc-btn mlc-btn-type-3 panel-btn' disabled>
        Ajout en cours…
      </button>
    )
  }

  return (
    <StockAlertButton
      disabled={disabled}
      customerEmail={customerEmail}
      onCreatedAlert={onCreatedAlert}
      variantId={variantId}
    />
  )
}

type groupedVariantsType = {
  [key: string]: VariantProps[]
}

// Display multiple tables when there are many variants by heading.
function DataTables({ headings, variants }: VariantsProps) {
  const groupedVariants = variants.reduce((acc, variant) => {
    const firstLabel = variant['labels'][0]
    const values = Object.hasOwn(acc, firstLabel) ? acc[firstLabel] : []

    return {
      ...acc,
      [firstLabel]: [...values, variant],
    }
  }, {} as groupedVariantsType)

  return (
    <div className='data-tables'>
      {Object.entries(groupedVariants).map(([key, values]) => (
        <div className='data-table' key={key}>
          <table>
            <thead>
              <tr>
                {headings.map((heading) => (
                  <th key={heading}>{heading}</th>
                ))}
                <th>Poids</th>
              </tr>
            </thead>
            <tbody>
              {values.map(({ labels, weight }) => (
                <tr key={labels.join('-')}>
                  {labels.map((label) => (
                    <td key={label}>{label}</td>
                  ))}
                  <td>{weight} g</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}
